import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const EMPTY_FORM = {
    etiqueta: '', nodo_id: '', mikrotik_id: '',
    marca: 'HUAWEI', modelo: '', version: '',
    ip: '', usuario: '', clave: '',
    puerto_nat: 2323, puerto_snmp: 3261, puerto_telnet: 23,
    snmp_lectura: '', snmp_escritura: '', activo: true
};

const Olt = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [nodos, setNodos] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [conexion, setConexion] = useState(null);
    const [search, setSearch] = useState('');
    const [probandoTelnet, setProbandoTelnet] = useState(false);
    const [probandoSnmp, setProbandoSnmp] = useState(false);
    const [resultTelnet, setResultTelnet] = useState(null);
    const [resultSnmp, setResultSnmp] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [oltRes, nodosRes, mkRes] = await Promise.all([
                api.get('/infraestructura/olt'),
                api.get('/infraestructura/nodos'),
                api.get('/infraestructura/mikrotik')
            ]);
            setItems(oltRes.data);
            setNodos(nodosRes.data);
            setMikrotiks(mkRes.data.filter(m => Number(m.activo)));
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM); setEditingId(null);
        setConexion(null); setResultTelnet(null); setResultSnmp(null);
        setError(''); setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            etiqueta: item.etiqueta || '', nodo_id: item.nodo_id || '',
            mikrotik_id: item.mikrotik_id || '', marca: item.marca || 'HUAWEI',
            modelo: item.modelo || '', version: item.version || '',
            ip: item.ip || '', usuario: item.usuario || '', clave: '',
            puerto_nat: item.puerto_nat || 2323,
            puerto_snmp: item.puerto_snmp || 3261,
            puerto_telnet: item.puerto_telnet || 23,
            snmp_lectura: item.snmp_lectura || '',
            snmp_escritura: item.snmp_escritura || '',
            activo: !!Number(item.activo)
        });
        setEditingId(item.id); setConexion(null);
        setResultTelnet(null); setResultSnmp(null);
        setError(''); setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setSaving(true); setError(''); setConexion(null);
        try {
            let res;
            if (editingId) {
                res = await api.put(`/infraestructura/olt/${editingId}`, form);
                showSuccess('OLT actualizada correctamente');
            } else {
                res = await api.post('/infraestructura/olt', form);
                showSuccess('OLT creada correctamente');
            }
            if (res.data?.conexion) setConexion(res.data.conexion);
            else setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando OLT');
        } finally {
            setSaving(false);
        }
    };

    const probarTelnet = async () => {
        if (!editingId) return;
        setProbandoTelnet(true); setResultTelnet(null);
        try {
            const { data } = await api.post(`/infraestructura/olt/${editingId}/probar`, { tipo: 'telnet' });
            setResultTelnet(data);
        } catch {
            setResultTelnet({ conectado: false, mensaje: 'Error de conexión' });
        } finally {
            setProbandoTelnet(false);
        }
    };

    const probarSnmp = async () => {
        if (!editingId) return;
        setProbandoSnmp(true); setResultSnmp(null);
        try {
            const { data } = await api.post(`/infraestructura/olt/${editingId}/probar`, { tipo: 'snmp' });
            setResultSnmp(data);
        } catch {
            setResultSnmp({ conectado: false, mensaje: 'Error de conexión' });
        } finally {
            setProbandoSnmp(false);
        }
    };

    const filtered = items.filter(i =>
        `${i.etiqueta} ${i.ip} ${i.modelo} ${i.nodo_nombre || ''}`.toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    const ResultBadge = ({ result }) => result ? (
        <div className={`mt-2 rounded-lg px-3 py-2 text-xs border ${result.conectado ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
            {result.conectado ? '✅' : '❌'} {result.mensaje}
            {result.modelo && <span className="ml-2 font-mono font-bold">{result.modelo}</span>}
            {result.firmware && <span className="ml-1 text-gray-500">({result.firmware})</span>}
        </div>
    ) : null;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Equipos OLT</h2>
                {can('inf_olt', 'create') && (
                    <button onClick={openCreate} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Conectar OLT
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="mb-4">
                <input type="text" placeholder="Buscar OLT..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron OLTs</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Etiqueta</th>
                                <th className="px-4 py-3 text-left">Marca / Modelo</th>
                                <th className="px-4 py-3 text-left">IP</th>
                                <th className="px-4 py-3 text-left">Puerto NAT</th>
                                <th className="px-4 py-3 text-left">Puerto SNMP</th>
                                <th className="px-4 py-3 text-left">MikroTik</th>
                                <th className="px-4 py-3 text-left">Nodo</th>
                                {can('inf_olt', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.etiqueta}</td>
                                    <td className="px-4 py-3 text-gray-600">{item.marca} {item.modelo}</td>
                                    <td className="px-4 py-3 text-gray-600 font-mono">{item.ip}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.puerto_nat || 2323}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.puerto_snmp || 3261}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.mikrotik_nombre || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">{item.nodo_nombre || '—'}</td>
                                    {can('inf_olt', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar OLT' : 'Conectar OLT'}</h3>
                            <button onClick={() => { setModalOpen(false); setConexion(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Etiqueta *</label>
                                    <input name="etiqueta" value={form.etiqueta} onChange={handleChange} className={inputClass} placeholder="Ej: OLT SAN ANTONIO" />
                                </div>
                                <div>
                                    <label className={labelClass}>Nodo</label>
                                    <select name="nodo_id" value={form.nodo_id} onChange={handleChange} className={inputClass}>
                                        <option value="">Sin nodo</option>
                                        {nodos.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>MikroTik asociada (puente de acceso)</label>
                                <select name="mikrotik_id" value={form.mikrotik_id} onChange={handleChange} className={inputClass}>
                                    <option value="">Sin MikroTik</option>
                                    {mikrotiks.map(m => <option key={m.id} value={m.id}>{m.etiqueta} ({m.ip})</option>)}
                                </select>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className={labelClass}>Marca</label>
                                    <input name="marca" value={form.marca} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Modelo</label>
                                    <input name="modelo" value={form.modelo} onChange={handleChange} className={inputClass} placeholder="Ej: MA5608T" />
                                </div>
                                <div>
                                    <label className={labelClass}>Versión / Firmware</label>
                                    <input name="version" value={form.version} onChange={handleChange} className={inputClass} placeholder="Ej: MA5600V800R015" />
                                </div>
                            </div>

                            {/* Conexión Telnet */}
                            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                <p className="text-sm font-semibold text-gray-700">🔌 Conexión Telnet</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>IP del CCR (NAT) *</label>
                                        <input name="ip" value={form.ip} onChange={handleChange} className={inputClass} placeholder="Ej: 10.10.18.2" />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Puerto NAT Telnet</label>
                                        <input name="puerto_nat" type="number" value={form.puerto_nat} onChange={handleChange} className={inputClass} />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Usuario OLT</label>
                                        <input name="usuario" value={form.usuario} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Contraseña OLT</label>
                                        <input name="clave" type="password" value={form.clave} onChange={handleChange} className={inputClass}
                                            placeholder={editingId ? "Dejar vacío para no cambiar" : ""} />
                                    </div>
                                </div>
                                {editingId && (
                                    <div>
                                        <button onClick={probarTelnet} disabled={probandoTelnet}
                                            className="text-sm font-medium px-4 py-2 rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 disabled:opacity-60">
                                            {probandoTelnet ? '⏳ Probando...' : '🔌 Probar Telnet'}
                                        </button>
                                        <ResultBadge result={resultTelnet} />
                                    </div>
                                )}
                            </div>

                            {/* Conexión SNMP */}
                            <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                                <p className="text-sm font-semibold text-gray-700">📡 Conexión SNMP</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Puerto SNMP (NAT UDP)</label>
                                        <input name="puerto_snmp" type="number" value={form.puerto_snmp} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Comunidad SNMP (lectura)</label>
                                        <input name="snmp_lectura" value={form.snmp_lectura} onChange={handleChange} className={inputClass} placeholder="Ej: public" />
                                    </div>
                                </div>
                                {editingId && (
                                    <div>
                                        <button onClick={probarSnmp} disabled={probandoSnmp}
                                            className="text-sm font-medium px-4 py-2 rounded-lg border border-green-300 text-green-600 hover:bg-green-50 disabled:opacity-60">
                                            {probandoSnmp ? '⏳ Probando...' : '📡 Probar SNMP'}
                                        </button>
                                        <ResultBadge result={resultSnmp} />
                                    </div>
                                )}
                            </div>

                            {editingId && (
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} id="activo_olt" />
                                    <label htmlFor="activo_olt" className="text-sm text-gray-700">OLT activa</label>
                                </div>
                            )}

                            {conexion && (
                                <div className={`rounded-lg px-4 py-3 text-sm border ${conexion.conectado ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
                                    <p className="font-semibold mb-1">{conexion.conectado ? '✅ Conexión exitosa' : '❌ Error de conexión'}</p>
                                    <p>{conexion.mensaje}</p>
                                    {conexion.conectado && conexion.slots?.length > 0 && (
                                        <div className="mt-2 text-xs">
                                            <p className="font-medium mb-1">Tarjetas instaladas:</p>
                                            {conexion.slots.map((s, i) => (
                                                <p key={i}>Slot {s.slot}: {s.board} — {s.status}</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => { setModalOpen(false); setConexion(null); }}
                                className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">
                                {conexion ? 'Cerrar' : 'Cancelar'}
                            </button>
                            {!conexion && (
                                <button onClick={handleSubmit} disabled={saving}
                                    className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                    style={{ backgroundColor: 'var(--color-primary)' }}>
                                    {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Conectar OLT')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Olt;
